"use client";

import { motion, useMotionTemplate, useMotionValue, useSpring } from "framer-motion";
import {
  Brain,
  Database,
  Globe,
  Monitor,
  Palette,
  Rocket,
  ShoppingCart,
  Smartphone
} from "lucide-react";
import { MouseEvent, useRef } from "react";

interface Service {
    icon: any;
    title: string;
    desc: string;
    color: string;
    iconAnim: string;
}

const SERVICES: Service[] = [
    {
        icon: Brain,
        title: "AI Integration & Development",
        desc: "Leverage LLMs and custom AI solutions to automate workflows and enhance decision-making.",
        color: "from-purple-500/10 to-indigo-500/10 group-hover:border-purple-500/40",
        iconAnim: "group-hover:scale-105 text-purple-600"
    },
    {
        icon: Globe,
        title: "Website Development",
        desc: "Modern, responsive, and SEO-optimized websites built with Next.js, React, and more.",
        color: "from-blue-500/10 to-cyan-500/10 group-hover:border-blue-500/40",
        iconAnim: "group-hover:scale-105 text-blue-600"
    },
    {
        icon: Smartphone,
        title: "Mobile App Development",
        desc: "Native and cross-platform mobile applications for iOS and Android using Flutter and React Native.",
        color: "from-emerald-500/10 to-teal-500/10 group-hover:border-emerald-500/40",
        iconAnim: "group-hover:scale-105 text-emerald-600"
    },
    {
        icon: Monitor,
        title: "Desktop Software",
        desc: "Powerful desktop applications for Windows, macOS, and Linux tailored to your business needs.",
        color: "from-amber-500/10 to-orange-500/10 group-hover:border-amber-500/40",
        iconAnim: "group-hover:scale-105 text-amber-600"
    },
    {
        icon: ShoppingCart,
        title: "E-commerce Solutions",
        desc: "Expert development in Shopify, WooCommerce, Magento, Medusa, and custom platforms.",
        color: "from-pink-500/10 to-rose-500/10 group-hover:border-pink-500/40",
        iconAnim: "group-hover:scale-105 text-pink-600"
    },
    {
        icon: Database,
        title: "ETL Pipelines",
        desc: "Robust data processing, migration, and transformation pipelines for data-driven insights.",
        color: "from-indigo-500/10 to-blue-500/10 group-hover:border-indigo-500/40",
        iconAnim: "group-hover:scale-105 text-indigo-600"
    },
    {
        icon: Rocket,
        title: "Fast MVP Development",
        desc: "Rapid prototyping and MVP development to validate your startup ideas quickly.",
        color: "from-red-500/10 to-orange-500/10 group-hover:border-red-500/40",
        iconAnim: "group-hover:scale-105 text-red-600"
    },
    {
        icon: Palette,
        title: "UI/UX Design",
        desc: "User-centered design focused on usability, aesthetics, and conversion optimization.",
        color: "from-violet-500/10 to-fuchsia-500/10 group-hover:border-violet-500/40",
        iconAnim: "group-hover:scale-105 text-violet-600"
    }
];

export default function HolographicCapabilities() {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 border-t border-gray-100">
            {/* Component Header */}
            <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="mb-16"
            >
                <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-gray-900 mb-4">
                    Core Capabilities<span className="text-purple-600">.</span>
                </h2>
                <p className="text-gray-500 max-w-xl text-lg font-medium">
                    Our elite engineering stack and capabilities, delivered with high fidelity, precision, and speed.
                </p>
            </motion.div>

            {/* Staggered Cascade Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {SERVICES.map((service, index) => (
                    <HolographicCard key={index} service={service} index={index} />
                ))}
            </div>
        </div>
    );
}

function HolographicCard({ service, index }: { service: Service; index: number }) {
    const Icon = service.icon;
    const cardRef = useRef<HTMLDivElement>(null);

    // Mouse positions for the premium radial lighting halo
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    // Dampen mouse movements with spring dynamics to avoid visual jumps
    const mouseXSpring = useSpring(mouseX, { damping: 25, stiffness: 180, mass: 0.6 });
    const mouseYSpring = useSpring(mouseY, { damping: 25, stiffness: 180, mass: 0.6 });

    function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
        const { left, top } = currentTarget.getBoundingClientRect();
        mouseX.set(clientX - left);
        mouseY.set(clientY - top);
    }

    const glowBackground = useMotionTemplate`radial-gradient(180px circle at ${mouseXSpring}px ${mouseYSpring}px, rgba(147, 51, 234, 0.08), transparent 80%)`;
    const borderBackground = useMotionTemplate`radial-gradient(120px circle at ${mouseXSpring}px ${mouseYSpring}px, rgba(147, 51, 234, 0.4), transparent 80%)`;

    return (
        <motion.div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{
                duration: 0.6,
                delay: (index % 4) * 0.08,
                ease: "easeOut"
            }}
            className="group relative border border-zinc-200/50 bg-white/30 hover:bg-white/50 backdrop-blur-lg rounded-[32px] p-8 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col justify-between h-full"
        >
            {/* Interactive Spotlight Radial Background */}
            <motion.div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"
                style={{ background: glowBackground }}
            />

            {/* Glowing Border Highlighter Overlay */}
            <motion.div
                className="absolute inset-[1px] pointer-events-none rounded-[31px] border border-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                    borderImage: borderBackground,
                    borderImageSlice: 1
                }}
            />

            {/* Faint Background Icon Watermark */}
            <div className="absolute right-[-20px] bottom-[-20px] opacity-[0.02] group-hover:opacity-[0.05] transition-opacity duration-500 z-0 pointer-events-none">
                <Icon className="w-40 h-40" />
            </div>

            {/* Card Content Area */}
            <div className="relative z-10">
                {/* Icon Container */}
                <div className="p-3 bg-gray-50/50 border border-gray-100 rounded-2xl w-fit mb-6 shadow-inner transition-colors group-hover:bg-white group-hover:shadow-md">
                    <Icon className={`w-6 h-6 transition-all duration-500 ease-out ${service.iconAnim}`} />
                </div>

                <h3 className="font-bold text-lg text-gray-900 leading-tight mb-3 tracking-tight">
                    {service.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">
                    {service.desc}
                </p>
            </div>
        </motion.div>
    );
}
